import { useLocation } from "@remix-run/react";
import { useHydrated } from "./useHydrated";

/**
 * Hook to dynamically determine the current route prefix and return appropriate routes
 * This allows the same components to work with different app proxy prefixes
 *
 * This hook handles both server-side rendering and client-side hydration:
 * - Server-side: Returns default routes to prevent hydration mismatches
 * - Client-side: Returns dynamic routes based on current prefix
 *
 * @returns {Object} Object containing route constants for the current prefix
 */
export function useRoutePrefix() {
  const location = useLocation();
  const isHydrated = useHydrated();

  // Default routes object to avoid repetition
  const getDefaultRoutes = () => ({
    APP_PROXY_ROOT: "/a/ui",
    QUIZ_ROUTE: "/a/ui/quiz",
    QUIZ_RESULTS_ROUTE: "/a/ui/quiz/results",
    currentPrefix: "ui",
  });

  // During SSR (server-side), return default routes to prevent hydration mismatch
  if (!isHydrated) {
    return getDefaultRoutes();
  }

  // After hydration (client-side), access app proxy config from window.ENV
  const appProxyConfig = window?.ENV?.APP_PROXY_CONFIG;

  // Safety check: if appProxyConfig is not available, return default routes
  if (!appProxyConfig || typeof appProxyConfig !== "object") {
    console.warn(
      "APP_PROXY_CONFIG not available, falling back to default routes",
    );
    return getDefaultRoutes();
  }

  // Extract prefix from pathname: /a/{prefix}/...
  const pathSegments = location.pathname.split("/");
  const prefix = pathSegments[2];

  // Early returns for invalid cases
  if (!prefix || !appProxyConfig[prefix]) {
    return getDefaultRoutes();
  }

  // For the main route (ui), always allow it
  // For dev routes, check if they're enabled
  if (prefix !== "ui" && !appProxyConfig[prefix]?.enabled) {
    return getDefaultRoutes();
  }

  // Get routes for the current prefix from the injected config
  const prefixConfig = appProxyConfig[prefix];

  // Safety check: ensure prefixConfig and routes exist
  if (!prefixConfig?.routes) {
    console.warn(
      `Invalid prefix config for ${prefix}, falling back to default routes`,
    );
    return getDefaultRoutes();
  }

  // Return dynamic routes for the current prefix
  return {
    APP_PROXY_ROOT: `/a/${prefix}`,
    QUIZ_ROUTE: prefixConfig.routes.quiz,
    QUIZ_RESULTS_ROUTE: prefixConfig.routes.results,
    currentPrefix: prefix,
  };
}
